import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';

export interface SpaceContributor {
	accountId: string;
	displayName: string;
	avatarUrl: string | undefined;
	jobTitle: string | undefined;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- not browser location
	location: string | undefined;
	isDeactivated: boolean;
	created: number;
	updated: number;
	commentCreated: number;
	viewed: number;
}

interface ActivityByUserEntry {
	accountId: string;
	created: number;
	updated: number;
	commentCreated: number;
	viewed: number;
}

interface ActivityByUserResponse {
	experimentalAnalyticsActivityByUser: {
		activityByUser: ActivityByUserEntry[];
	};
}

// AGG batch user query for resolving display names and avatars
// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- standalone AGG query, not a Relay operation
const FetchUsersBatchQuery = gql`
	query spaceContributorsFetchUsersBatchQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
			accountStatus
		}
	}
`;

interface AGGUser {
	accountId: string;
	name: string | null;
	picture: string | null;
	accountStatus: 'active' | 'inactive' | 'closed';
}

interface FetchUsersBatchData {
	users?: (AGGUser | null)[] | null;
}

interface UserProfile {
	displayName: string;
	avatarUrl: string | undefined;
	jobTitle: string | undefined;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- not browser location
	location: string | undefined;
	isDeactivated: boolean;
}

const AGG_USERS_BATCH_LIMIT = 90;

const normalizeLocation = (location: string | null | undefined): string | undefined => {
	if (!location) {
		return undefined;
	}
	return location.replace(/^Home office:\s*/i, '').trim() || undefined;
};

const MIN_ACTIVITY_THRESHOLD = 5;

const hasSignificantActivity = (entry: ActivityByUserEntry): boolean =>
	(entry.created ?? 0) >= MIN_ACTIVITY_THRESHOLD || (entry.updated ?? 0) >= MIN_ACTIVITY_THRESHOLD;

const compareContributors = (a: ActivityByUserEntry, b: ActivityByUserEntry): number => {
	// Pages created is the primary sort (more superior)
	const createdDiff = (b.created ?? 0) - (a.created ?? 0);
	if (createdDiff !== 0) {
		return createdDiff;
	}
	// Edits (updated) as secondary sort
	return (b.updated ?? 0) - (a.updated ?? 0);
};

const fetchUserProfiles = async (
	accountIds: string[],
	signal: AbortSignal,
): Promise<Map<string, UserProfile>> => {
	const profileMap = new Map<string, UserProfile>();

	if (accountIds.length === 0 || signal.aborted) {
		return profileMap;
	}

	const batches: string[][] = [];
	for (let i = 0; i < accountIds.length; i += AGG_USERS_BATCH_LIMIT) {
		batches.push(accountIds.slice(i, i + AGG_USERS_BATCH_LIMIT));
	}

	const processBatch = async (batch: string[]) => {
		if (signal.aborted) {
			return;
		}

		try {
			const { data, error } = await queryInterop<FetchUsersBatchData, { accountIds: string[] }>({
				query: FetchUsersBatchQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: { accountIds: batch },
				interopApolloAggClient: true,
			});

			if (error?.graphQLErrors) {
				error.graphQLErrors.forEach(markErrorAsHandled);
			}

			const users = data?.users ?? [];
			for (const user of users) {
				if (user !== null) {
					profileMap.set(user.accountId, {
						displayName: user.name ?? user.accountId,
						avatarUrl: user.picture ?? undefined,
						jobTitle: undefined,
						location: undefined,
						isDeactivated: user.accountStatus !== 'active',
					});
				}
			}
		} catch {
			// Silently handle batch failures
		}
	};

	await Promise.all(batches.map(processBatch));

	return profileMap;
};

interface AtlassianUserProfile {
	title: string | null;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- not browser location
	location: string | null;
}

const buildAliasedUserQuery = (accountIds: string[]): string => {
	const fields = accountIds
		.map((id, index) => `user_${index}: confluence_atlassianUser(id: "${id}") { title location }`)
		.join('\n');
	return `query spaceContributorsProfileDetailsQuery { ${fields} }`;
};

const ALIASED_BATCH_LIMIT = 25;

const fetchUserProfileDetails = async (
	accountIds: string[],
	signal: AbortSignal,
): Promise<Map<string, AtlassianUserProfile>> => {
	const detailsMap = new Map<string, AtlassianUserProfile>();

	if (accountIds.length === 0 || signal.aborted) {
		return detailsMap;
	}

	const batches: string[][] = [];
	for (let i = 0; i < accountIds.length; i += ALIASED_BATCH_LIMIT) {
		batches.push(accountIds.slice(i, i + ALIASED_BATCH_LIMIT));
	}

	const processBatch = async (batch: string[]) => {
		if (signal.aborted) {
			return;
		}

		try {
			const queryString = buildAliasedUserQuery(batch);
			const { data, error } = await queryInterop<
				Record<string, AtlassianUserProfile | null>,
				Record<string, never>
			>({
				query: gql(queryString) as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: {},
				interopApolloAggClient: true,
			});

			if (error?.graphQLErrors) {
				error.graphQLErrors.forEach(markErrorAsHandled);
			}

			if (data) {
				batch.forEach((accountId, index) => {
					const user = data[`user_${index}`];
					if (user) {
						detailsMap.set(accountId, {
							title: user.title,
							location: user.location,
						});
					}
				});
			}
		} catch {
			// Silently handle batch failures
		}
	};

	await Promise.all(batches.map(processBatch));

	return detailsMap;
};

interface UseSpaceContributorsResult {
	contributors: SpaceContributor[];
	loading: boolean;
	error: Error | null;
}

const TODAY = new Date();
const ONE_YEAR_AGO = new Date(TODAY);
ONE_YEAR_AGO.setFullYear(TODAY.getFullYear() - 1);
const FROM_DATE = ONE_YEAR_AGO.toISOString().split('T')[0];
const TO_DATE = TODAY.toISOString().split('T')[0];
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EMPTY_VARIABLES: Record<string, string> = {};

const parseActivityResult = (json: unknown) =>
	({ experimentalAnalyticsActivityByUser: json }) as ActivityByUserResponse;

export const useSpaceContributors = (spaceKey: string): UseSpaceContributorsResult => {
	const { cloudId } = useSessionData();
	const spaceId = useSpaceId(spaceKey);
	const [contributors, setContributors] = useState<SpaceContributor[]>([]);
	const [profilesLoading, setProfilesLoading] = useState(true);
	const abortControllerRef = useRef<AbortController | null>(null);

	const url = useMemo(() => {
		if (!cloudId || !spaceId) {
			return '';
		}

		const params = new URLSearchParams({
			fromDate: FROM_DATE,
			toDate: TO_DATE,
			timezone: TIMEZONE,
			content: 'page,livedoc',
			spaceId,
			sortBy: 'created',
			sortOrder: 'desc',
			limit: '50',
		});

		return `/gateway/api/ex/confluence/${cloudId}/analytics/v2/activityByUser?${params.toString()}`;
	}, [cloudId, spaceId]);

	const {
		data,
		loading: activityLoading,
		error,
	} = useExperimentalQuery<ActivityByUserResponse, Record<string, string>, Error>({
		url,
		method: 'GET',
		credentials: 'include',
		headers: { 'Content-Type': MediaType.JSON },
		parseResult: parseActivityResult,
		skip: !cloudId || !spaceId,
		variables: EMPTY_VARIABLES,
	});

	const activeEntries = useMemo(
		() =>
			(data?.experimentalAnalyticsActivityByUser?.activityByUser ?? [])
				.filter(hasSignificantActivity)
				.sort(compareContributors),
		[data],
	);

	const resolveProfiles = useCallback(async (entries: ActivityByUserEntry[]) => {
		// Cancel any in-flight profile fetch
		abortControllerRef.current?.abort();
		const controller = new AbortController();
		abortControllerRef.current = controller;

		setProfilesLoading(true);
		try {
			const accountIds = entries.map((entry) => entry.accountId);
			const [profileMap, detailsMap] = await Promise.all([
				fetchUserProfiles(accountIds, controller.signal),
				fetchUserProfileDetails(accountIds, controller.signal),
			]);

			if (!controller.signal.aborted) {
				const resolved: SpaceContributor[] = entries.map((entry) => {
					const profile = profileMap.get(entry.accountId);
					const details = detailsMap.get(entry.accountId);
					return {
						...entry,
						displayName: profile?.displayName ?? entry.accountId,
						avatarUrl: profile?.avatarUrl,
						jobTitle: details?.title ?? undefined,
						location: normalizeLocation(details?.location),
						isDeactivated: profile?.isDeactivated ?? false,
					};
				});

				setContributors(resolved);
			}
		} finally {
			setProfilesLoading(false);
		}
	}, []);

	useEffect(() => {
		if (activeEntries.length > 0) {
			void resolveProfiles(activeEntries);
		} else if (!activityLoading) {
			setContributors([]);
		}

		return () => {
			abortControllerRef.current?.abort();
		};
	}, [activeEntries, resolveProfiles, activityLoading]);

	return {
		contributors,
		loading: activityLoading || profilesLoading,
		error: error ?? null,
	};
};
